import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { queryKeysUnit } from '../queryKeys';

import { fetchUnitMappingByIdentifierAndIssuer } from './fetchUnitMappingByIdentifierAndIssuer';
import {
  type UnitMappingByIssuerAndIdentifierPayload,
  type UnitMappingByIssuerAndIdentifierResponse,
} from './types';

/**
 * React Query based custom hook for getting an existing unit mapping for the given issuer and unit identifier.
 * @param unitMappingContext - The context (issuer and unit identifier) of the unit mapping to fetch.
 * @param options - Optional configuration for the query.
 * @returns The query result containing the unit mapping data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/get_unit_mapping_for_identifier_and_issuer_units_unit_mapping_query_get
 */
export const useQueryUnitMappingByIdentifierAndIssuer = (
  unitMappingContext: UnitMappingByIssuerAndIdentifierPayload,
  options?: Omit<
    UseQueryOptions<UnitMappingByIssuerAndIdentifierResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<UnitMappingByIssuerAndIdentifierResponse>({
    enabled: Object.values(unitMappingContext).every(Boolean),
    async queryFn({ queryKey }) {
      const unitMappingContext = {
        ...(queryKey[4] as UnitMappingByIssuerAndIdentifierPayload), // TODO: query keys should be structured as one object as first array element; getting queryKey[4] is brittle
      };

      return fetchUnitMappingByIdentifierAndIssuer(unitMappingContext);
    },
    queryKey: queryKeysUnit.getMappingByIssuerAndIdentifier(unitMappingContext),
    ...options,
  });
};
